import {
  Alert,
  Avatar,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Ban, Check, Copy, ShieldCheck, Trash2, UserCheck } from 'lucide-react'
import { useState } from 'react'

import { ApiError, ERROR_CODES, errorMessage } from '@/api/errors'
import { useDeleteUser, usePatchUser, useUser } from '@/api/queries'
import type { AdminUser, UserRole } from '@/api/types'
import { useCurrentAdmin } from '@/auth/use-session'
import { PageHeader } from '@/components/layout/app-shell'
import { UserCardsPanel } from '@/features/users/user-cards-panel'
import { RoleBadge, StateBadge } from '@/features/users/user-badges'
import {
  SEX_LABELS,
  avatarName,
  displayName,
  formatDateTime,
  formatNumber,
  formatRelative,
  plural,
} from '@/lib/format'
import { useCopyToClipboard } from '@/lib/hooks'
import { notify } from '@/lib/toast'

export const Route = createFileRoute('/_app/utenti/$userId')({
  component: UserDetailPage,
})

function UserDetailPage() {
  const { userId } = Route.useParams()
  const query = useUser(userId)

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Utente" />
        <div className="space-y-4">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </>
    )
  }

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404
    return (
      <>
        <PageHeader
          title="Utente"
          actions={
            <Button variant="ghost" asChild>
              <Link to="/utenti">
                <ArrowLeft />
                Torna agli utenti
              </Link>
            </Button>
          }
        />
        <ErrorState
          title={notFound ? 'Utente non trovato' : 'Caricamento non riuscito'}
          message={errorMessage(query.error)}
          requestId={query.error instanceof ApiError ? query.error.requestId : undefined}
          {...(notFound ? {} : { onRetry: () => void query.refetch() })}
        />
      </>
    )
  }

  return <UserDetail user={query.data} />
}

function UserDetail({ user }: { user: AdminUser }) {
  const admin = useCurrentAdmin()
  /**
   * Un admin non può modificare il **proprio** ruolo: il server risponde
   * `409 cannot_modify_self`, per non rimuovere l'ultimo accesso amministrativo.
   * Il controllo è replicato qui solo per disabilitare i comandi invece di far
   * scoprire il divieto con un errore.
   */
  const isSelf = admin?.id === user.id

  const [copied, copy] = useCopyToClipboard()
  const [disableOpen, setDisableOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const patch = usePatchUser()

  const changeRole = async (role: UserRole) => {
    if (role === user.role) return
    try {
      await patch.mutateAsync({ id: user.id, patch: { role } })
      notify({
        message: role === 'admin' ? 'Ruolo admin assegnato.' : 'Ruolo utente assegnato.',
        variant: 'success',
      })
    } catch (error) {
      if (error instanceof ApiError && error.code === ERROR_CODES.cannotModifySelf) {
        notify({ message: error.message, variant: 'danger' })
        return
      }
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  const enable = async () => {
    try {
      await patch.mutateAsync({ id: user.id, patch: { disabled: false } })
      notify({ message: 'Account riabilitato.', variant: 'success' })
    } catch (error) {
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  return (
    <>
      <PageHeader
        title={displayName(user)}
        description={user.email}
        actions={
          <Button variant="ghost" asChild>
            <Link to="/utenti">
              <ArrowLeft />
              Torna agli utenti
            </Link>
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-4">
          {/* Identità */}
          <Card>
            <CardContent className="pt-4 sm:pt-5">
              <div className="flex items-start gap-4">
                <Avatar name={avatarName(displayName(user))} size="lg" />

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StateBadge user={user} />
                    <RoleBadge user={user} />
                  </div>

                  <button
                    type="button"
                    onClick={() => void copy(user.id)}
                    className="text-muted-foreground hover:text-foreground inline-flex max-w-full items-center gap-1.5 rounded-sm text-xs transition-colors"
                    aria-label="Copia l'id utente"
                  >
                    <code className="truncate font-mono">{user.id}</code>
                    {copied ? (
                      <Check className="text-success-content size-3.5 shrink-0" />
                    ) : (
                      <Copy className="size-3.5 shrink-0" />
                    )}
                  </button>
                </div>
              </div>

              {user.disabled && user.disabledReason ? (
                <Alert variant="danger" title="Motivo della disabilitazione" className="mt-4">
                  {user.disabledReason}
                  <p className="mt-1 text-xs">
                    Annotazione interna: non viene mostrata all'utente.
                  </p>
                </Alert>
              ) : null}
            </CardContent>
          </Card>

          {/* Profilo */}
          <Card>
            <CardHeader>
              <div className="space-y-1">
                <CardTitle>Profilo</CardTitle>
                <CardDescription>
                  Dati antropometrici raccolti durante l'onboarding. Il pannello non li modifica.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {user.profile ? (
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <Detail label="Nome" value={user.profile.name} />
                  <Detail label="Sesso" value={SEX_LABELS[user.profile.sex]} />
                  <Detail label="Età" value={user.profile.age ? `${user.profile.age} anni` : '—'} />
                  <Detail
                    label="Peso"
                    value={user.profile.weightKg ? `${user.profile.weightKg} kg` : '—'}
                  />
                  <Detail
                    label="Altezza"
                    value={user.profile.heightCm ? `${user.profile.heightCm} cm` : '—'}
                  />
                  <Detail label="Aggiornato" value={formatRelative(user.profile.updatedAt)} />
                </dl>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Nessun profilo: l'utente è registrato ma non ha completato l'onboarding.
                </p>
              )}
            </CardContent>
          </Card>

          <UserCardsPanel userId={user.id} />
        </div>

        <div className="space-y-4">
          {/* Attività */}
          <Card>
            <CardHeader>
              <CardTitle>Attività</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                <Row label="Schede" value={formatNumber(user.cardCount)} numeric />
                <Row label="Ultimo accesso" value={formatRelative(user.lastLoginAt)} />
                <Row label="Registrato" value={formatDateTime(user.createdAt)} />
                <Row label="Email verificata" value={user.emailVerified ? 'Sì' : 'No'} />
                <Row
                  label="Onboarding"
                  value={user.onboardingCompleted ? 'Completato' : 'Incompleto'}
                />
              </dl>
            </CardContent>
          </Card>

          {/* Ruolo */}
          <Card>
            <CardHeader>
              <div className="space-y-1">
                <CardTitle>Ruolo</CardTitle>
                <CardDescription>Si cambia solo da qui, mai dall'utente.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select
                aria-label={`Ruolo di ${user.email}`}
                value={user.role}
                onValueChange={(role) => void changeRole(role as UserRole)}
                disabled={isSelf || patch.isPending}
                items={[
                  { value: 'user', label: 'Utente' },
                  { value: 'admin', label: 'Admin' },
                ]}
              />
              {isSelf ? (
                <p className="text-muted-foreground text-xs">
                  Non puoi cambiare il tuo stesso ruolo: serve a non restare senza accessi
                  amministrativi.
                </p>
              ) : user.role === 'user' ? (
                <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
                  <ShieldCheck className="mt-px size-3.5 shrink-0" aria-hidden />
                  Un admin accede ai dati di tutti gli utenti.
                </p>
              ) : null}
            </CardContent>
          </Card>

          {/* Azioni sull'account */}
          <Card className="border-danger/30">
            <CardHeader>
              <div className="space-y-1">
                <CardTitle>Moderazione</CardTitle>
                <CardDescription>Azioni che l'utente subisce subito.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {user.disabled ? (
                <Button variant="outline" loading={patch.isPending} onClick={() => void enable()}>
                  <UserCheck />
                  Riabilita account
                </Button>
              ) : (
                <Button
                  variant="danger-outline"
                  disabled={isSelf}
                  onClick={() => setDisableOpen(true)}
                >
                  <Ban />
                  Disabilita account
                </Button>
              )}

              <Button
                variant="danger-outline"
                disabled={isSelf}
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 />
                Elimina utente
              </Button>

              {isSelf ? (
                <p className="text-muted-foreground text-xs">
                  Non puoi disabilitare né eliminare il tuo stesso account.
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <DisableDialog user={user} open={disableOpen} onOpenChange={setDisableOpen} />
      <DeleteUserDialog user={user} open={deleteOpen} onOpenChange={setDeleteOpen} />
    </>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium">{value}</dd>
    </div>
  )
}

function Row({ label, value, numeric }: { label: string; value: string; numeric?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-right font-medium" {...(numeric ? { 'data-numeric': true } : {})}>
        {value}
      </dd>
    </div>
  )
}

/**
 * Disabilitazione con annotazione.
 *
 * Disabilitare revoca **subito** tutti i refresh token; gli access token già emessi
 * restano formalmente validi fino a scadenza (15 minuti), ed è giusto dirlo a chi
 * modera: "l'ho bloccato ma è ancora dentro" altrimenti sembra un bug.
 */
function DisableDialog({
  user,
  open,
  onOpenChange,
}: {
  user: AdminUser
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const patch = usePatchUser()

  const onConfirm = async () => {
    try {
      await patch.mutateAsync({
        id: user.id,
        patch: { disabled: true, ...(reason.trim() ? { disabledReason: reason.trim() } : {}) },
      })
      notify({
        message: 'Account disabilitato: le sessioni attive sono state revocate.',
        variant: 'success',
      })
      setReason('')
      onOpenChange(false)
    } catch (error) {
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Disabilitare l'account?</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Alert variant="warning" title="Effetto immediato, con una coda">
            I refresh token vengono revocati subito e i login successivi vengono rifiutati. Un
            access token già emesso può restare valido fino a 15 minuti.
          </Alert>

          <Field
            label="Motivo"
            hint="Annotazione interna, visibile solo agli admin. Non viene mostrata all'utente."
          >
            {(props) => (
              <Textarea
                {...props}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Segnalazioni ripetute di contenuti inappropriati…"
              />
            )}
          </Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button variant="danger" loading={patch.isPending} onClick={() => void onConfirm()}>
            Disabilita
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Eliminazione definitiva.
 *
 * Chiede di riscrivere l'email: è un'operazione irreversibile su dati di una persona
 * reale, e un solo click di conferma è troppo poco per distinguerla da un'azione
 * ordinaria.
 */
function DeleteUserDialog({
  user,
  open,
  onOpenChange,
}: {
  user: AdminUser
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [confirmation, setConfirmation] = useState('')
  const remove = useDeleteUser()
  const navigate = useNavigate()

  const matches = confirmation.trim().toLowerCase() === user.email.toLowerCase()

  const close = (next: boolean) => {
    if (!next) setConfirmation('')
    onOpenChange(next)
  }

  const onConfirm = async () => {
    if (!matches) return
    try {
      await remove.mutateAsync(user.id)
      notify({ message: 'Utente eliminato.', variant: 'success' })
      close(false)
      await navigate({ to: '/utenti' })
    } catch (error) {
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Eliminare l'utente?</DialogTitle>
          <DialogDescription>
            Vengono rimossi account, profilo, schede e sessioni Coach. Irreversibile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {typeof user.cardCount === 'number' && user.cardCount > 0 ? (
            <Alert
              variant="danger"
              title={`${plural(user.cardCount, 'scheda', 'schede')} verranno perse`}
            >
              Se il problema è l'accesso e non i dati, disabilitare l'account è reversibile.
            </Alert>
          ) : null}

          <Field label="Riscrivi l'email per confermare" required>
            {(props) => (
              <Input
                {...props}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                placeholder={user.email}
                autoComplete="off"
                spellCheck={false}
              />
            )}
          </Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            Annulla
          </Button>
          <Button
            variant="danger"
            disabled={!matches}
            loading={remove.isPending}
            onClick={() => void onConfirm()}
          >
            Elimina definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
